/**
 * Surf presenter remote — channel helpers over AppSync NONE pub/sub.
 * publish = mutation (HTTP); subscribe = realtime WS subscription (owner-scoped).
 */
import { surfGraphQLFetch } from "../graphql/httpClient.js";
import { SurfRealtimeClient } from "../graphql/realtimeClient.js";
import { PRESENT_GRAPHQL, normalizePresentSignal, } from "./presentSignal.js";
function reportChannelError(cfg, err, phase) {
    console.warn(`[present] ${phase} failed`, err);
    cfg.onError?.(err, phase);
}
/** Client-side pointer throttle target (~20 Hz). */
export const POINTER_THROTTLE_MS = 50;
/** Heartbeat cadence: phone republishes current index to keep the display aligned. */
export const HEARTBEAT_INTERVAL_MS = 4_000;
/** Peer considered gone if no signal within this window. */
export const PEER_TIMEOUT_MS = 12_000;
/** Publish a single presenter signal (mutation, no persistence). */
export async function publishSignal(cfg, input) {
    const idToken = await cfg.getIdToken();
    const result = await surfGraphQLFetch({
        endpoint: cfg.endpoint,
        idToken,
        query: PRESENT_GRAPHQL.sendPresentSignal,
        variables: { input },
        resultKey: "sendPresentSignal",
    });
    return normalizePresentSignal(result);
}
/**
 * Subscribe to the owner-scoped signal stream for a session.
 * Returns an unsubscribe function that also tears down the WS client.
 */
export function subscribeSignals(cfg, sessionId, onSignal, opts) {
    const client = new SurfRealtimeClient({
        graphqlEndpoint: cfg.endpoint,
        realtimeEndpoint: cfg.realtimeEndpoint,
        getIdToken: cfg.getIdToken,
        onStatusChange: opts?.onStatus,
        onError: (err) => reportChannelError(cfg, err, "subscribe"),
    });
    const unsubscribe = client.subscribe(PRESENT_GRAPHQL.onPresentSignal, { sessionId }, (raw) => {
        const signal = normalizePresentSignal(raw);
        if (signal)
            onSignal(signal);
    });
    void client.connect().catch((err) => {
        reportChannelError(cfg, err, "subscribe");
    });
    return () => {
        unsubscribe();
        client.disconnect();
    };
}
/**
 * Throttle a publisher so bursty pointer moves are coalesced to ~POINTER_THROTTLE_MS.
 * The latest call within the window is always flushed (trailing edge).
 */
export function createThrottledPublisher(cfg, intervalMs = POINTER_THROTTLE_MS) {
    let lastSentAt = 0;
    let pending = null;
    let timer = null;
    const doSend = (input) => {
        lastSentAt = Date.now();
        void publishSignal(cfg, input).catch((err) => {
            reportChannelError(cfg, err, "publish");
        });
    };
    const flush = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        if (pending) {
            const input = pending;
            pending = null;
            doSend(input);
        }
    };
    const send = (input) => {
        const now = Date.now();
        const elapsed = now - lastSentAt;
        if (elapsed >= intervalMs) {
            pending = null;
            doSend(input);
            return;
        }
        pending = input;
        if (!timer) {
            timer = setTimeout(() => {
                timer = null;
                flush();
            }, intervalMs - elapsed);
        }
    };
    const cancel = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        pending = null;
    };
    return { send, flush, cancel };
}
/**
 * Start a heartbeat that republishes the current signal on a fixed cadence.
 * `getSignal` returns the latest signal to send (usually a `goto` at the current index),
 * or null to skip a tick. Returns a stop function.
 */
export function startHeartbeat(cfg, getSignal, intervalMs = HEARTBEAT_INTERVAL_MS) {
    const timer = setInterval(() => {
        const input = getSignal();
        if (input) {
            void publishSignal(cfg, input).catch((err) => {
                reportChannelError(cfg, err, "publish");
            });
        }
    }, intervalMs);
    return () => clearInterval(timer);
}
